
import { useState, useEffect, useRef } from "react";
import { AddPixelDialog } from "@/components/dashboard/AddPixelDialog";
import { 
  ChartBar, 
  Target, 
  User, 
  DollarSign,
  Sparkles
} from "lucide-react";

interface EmptyStateProps {
  hasPixels: boolean;
  onUpgradeClick?: () => void;
}

export const EmptyState = ({ hasPixels }: EmptyStateProps) => {
  const [showAddPixelDialog, setShowAddPixelDialog] = useState(false);
  const [isDialogSubmitting, setIsDialogSubmitting] = useState(false);
  const [isInstallationPhase, setIsInstallationPhase] = useState(false);
  const dialogStateRef = useRef({ submitting: false, installation: false });
  const preventCloseUntilRef = useRef(0);

  useEffect(() => {
    dialogStateRef.current = {
      submitting: isDialogSubmitting,
      installation: isInstallationPhase
    };

    if (isDialogSubmitting || isInstallationPhase) {
      preventCloseUntilRef.current = Date.now() + 2000;
      
      if (!showAddPixelDialog) {
        setShowAddPixelDialog(true);
      }
    }
  }, [showAddPixelDialog, isDialogSubmitting, isInstallationPhase]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDialogSubmitting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDialogSubmitting]);

  const handleOpenChange = (open: boolean) => {
    const currentTime = Date.now();
    const isTimeLocked = currentTime < preventCloseUntilRef.current;
    
    if (!open && (isDialogSubmitting || isInstallationPhase || isTimeLocked)) {
      return;
    }
    
    setShowAddPixelDialog(open);
    
    if (!open && !isDialogSubmitting && !isInstallationPhase && !isTimeLocked) {
      preventCloseUntilRef.current = 0;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] max-w-lg mx-auto text-center p-6">
      <h2 className="text-2xl font-semibold text-gray-900 mb-3">
        Get Started with LeadExtract
      </h2>
      <p className="text-gray-600 mb-8">
        Adding a tracking pixel to your website unlocks powerful insights and opportunities.
      </p>
      
      <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-800 mb-4">Benefits of Adding a Pixel:</h3>
        
        <ul className="space-y-4 text-left">
          <li className="flex items-start">
            <div className="mr-3 mt-0.5 bg-blue-100 p-1.5 rounded-full">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">Identify Website Visitors</p>
              <p className="text-sm text-gray-600">See which companies are visiting your website in real-time.</p>
            </div>
          </li>
          
          <li className="flex items-start">
            <div className="mr-3 mt-0.5 bg-green-100 p-1.5 rounded-full">
              <Sparkles className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">AI Insights</p>
              <p className="text-sm text-gray-600">Get intelligent recommendations and analysis on your website visitors.</p>
            </div>
          </li>
          
          <li className="flex items-start">
            <div className="mr-3 mt-0.5 bg-amber-100 p-1.5 rounded-full">
              <DollarSign className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">Increase Conversion Rates</p>
              <p className="text-sm text-gray-600">Target your sales efforts toward visitors most likely to convert.</p>
            </div>
          </li>
        </ul>
      </div>
      
      <AddPixelDialog
        open={showAddPixelDialog}
        onOpenChange={handleOpenChange}
        onSubmitStateChange={(submitting) => {
          setIsDialogSubmitting(submitting);
          if (submitting) {
            preventCloseUntilRef.current = Date.now() + 5000;
          }
        }}
        onInstallationPhaseChange={(inInstallation) => {
          setIsInstallationPhase(inInstallation);
          if (inInstallation) {
            preventCloseUntilRef.current = Date.now() + 10000;
          }
        }}
      />
    </div>
  );
}
